"use client"; // Tiptap's useEditor hook must run on the client

import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";

// Tiptap Imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';

// Your App's Types
import { QAType } from "utils/types/Provider.types"; // Adjust path as needed

interface QADialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (qa: QAType) => void;
  initialData?: QAType | null;
}

const makeEmptyQA = (): QAType => ({
  id: ``, // ID will be set on save
  question: "",
  answer: "",
});

/**
 * A simple Tiptap toolbar using Material-UI ToggleButtons
 */
const TiptapToolbar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  return (
    <Paper elevation={0} sx={{ p: 1, borderBottom: '1px solid #ddd', display: 'flex', flexWrap: 'wrap' }}>
      <ToggleButtonGroup size="small" aria-label="text formatting">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup size="small" aria-label="text alignment" sx={{ ml: 1 }}>
        <ToggleButton
          value="left"
          selected={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          selected={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          selected={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <FormatAlignRight />
        </ToggleButton>
        <ToggleButton
          value="justify"
          selected={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <FormatAlignJustify />
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
};

export const QADialog: React.FC<QADialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  // Store the non-editor fields in state
  const [question, setQuestion] = useState("");
  const [id, setId] = useState<string>("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    // The 'answer' (HTML) is managed by the editor
    content: initialData?.answer || "", 
  });

  useEffect(() => {
    // Reset form when the dialog is opened or initialData changes
    if (open) {
      const data = initialData || makeEmptyQA();
      setId(data.id);
      setQuestion(data.question);
      // Reset the editor's content
      if (editor && editor.getHTML() !== data.answer) {
        editor.commands.setContent(data.answer, { emitUpdate: false });
      }
    }
  }, [initialData, open, editor]);

  const handleSave = () => {
    if (!editor) return;

    // Combine state and editor content to build the final object
    onSave({
      id: id,
      question: question,
      answer: editor.getHTML(), // Get the latest HTML from Tiptap
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{initialData?.id ? "Modifier" : "Ajouter"} Q&A</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Question"
            name="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            fullWidth
            margin="none"
          />
          
          {/* Tiptap Editor for the Answer */}
          {!editor ? (
             <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
             </Box>
          ) : (
            <Paper variant="outlined">
              <TiptapToolbar editor={editor} />
              <EditorContent 
                editor={editor} 
                style={{ minHeight: '200px', padding: '16px', outline: 'none' }} 
              />
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">Annuler</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Sauvegarder
        </Button>
      </DialogActions>
    </Dialog>
  );
};